import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import Error from '../Error';
import BillingoDocumentDetailsModal from './BillingoDocumentDetailsModal';
import BillingoCreateSzamlaModal from './BillingoCreateSzamlaModal';
import BillingoSendModal from './BillingoSendModal';
import StornoModal from './StornoModal';
import { useInvoicePdf } from './openInvoicePdf';
import { DOC_TYPES } from '../../Utils/billingoDocuments';
import { finalInvoiceBlockedReason } from '../../Utils/tripClaims';

/**
 * What can be done to a listed Billingo document — create the Számla from a
 * Díjbekérő, send it, open its PDF, cancel it (sztornó), open its details —
 * for every table of documents (an order's documents, the Invoicing list of
 * all of them). THE one place that decides: a row's icons and the details
 * modal's buttons are both `actionsOf(doc)`, so they cannot disagree. Each
 * table lays out its own columns.
 *
 * `finalBlocked` (why a Számla can't be issued yet) and `defaultEmail` are
 * what a caller holding the full booking knows; without them each document's
 * own bookings answer — their status and email ride on its booking context.
 * `onChanged` runs after anything is issued, sent or cancelled.
 *
 * Returns `actionsFor(doc)` (the row's buttons), `openDetails(doc)`, `alert`
 * (render above the table: why a PDF didn't open) and `modals` (render once,
 * beside the table).
 */
export default function useDocumentActions({ onChanged, finalBlocked = '', defaultEmail = '' }) {
  const [sendDoc, setSendDoc] = useState(null);
  const [szamlaDoc, setSzamlaDoc] = useState(null);
  const [stornoDocs, setStornoDocs] = useState(null);
  const [detailsDoc, setDetailsDoc] = useState(null);
  const pdf = useInvoicePdf();

  const actionsOf = (doc) => {
    const live = Boolean(doc.billingo_document_id) && doc.status !== 'cancelled';
    const blocked = finalBlocked || finalInvoiceBlockedReason(doc.bookings || []);
    const opening = pdf.opening === doc.id;
    return [
      // A converted Díjbekérő already has its Számla; a second is a duplicate.
      live && doc.type === DOC_TYPES.DIJBEKERO && !doc.converted && {
        key: 'szamla',
        label: 'Create Számla',
        icon: 'fa-file-invoice',
        tone: 'primary',
        title: blocked || 'Create Számla',
        disabled: Boolean(blocked),
        run: () => setSzamlaDoc(doc),
      },
      live && {
        key: 'send', label: 'Send to customer', icon: 'fa-paper-plane', run: () => setSendDoc(doc),
      },
      (doc.pdf_url || doc.billingo_document_id) && {
        key: 'pdf',
        label: opening ? 'Opening PDF…' : 'Open PDF',
        icon: opening ? 'fa-spinner fa-spin' : 'fa-external-link-alt',
        disabled: opening,
        // The PDF opens in its own tab; the details stay where they are.
        keepsDetailsOpen: true,
        run: () => pdf.open(doc),
      },
      live && {
        key: 'storno', label: 'Cancel (sztornó)', icon: 'fa-ban', tone: 'danger', run: () => setStornoDocs([doc]),
      },
    ].filter(Boolean);
  };

  const actionsFor = (doc) => actionsOf(doc).map((action) => (
    <Button
      key={action.key}
      variant="link"
      className={`p-0 me-2${action.tone === 'danger' ? ' text-danger' : ''}`}
      aria-label={action.label}
      title={action.title || action.label}
      disabled={action.disabled}
      onClick={action.run}
    >
      <i className={`fa ${action.icon}`} />
    </Button>
  ));

  const closeDetails = () => {
    setDetailsDoc(null);
    pdf.setError('');
  };

  const alert = <Error error={pdf.error} setError={pdf.setError} />;

  const modals = (
    <>
      <BillingoSendModal
        show={!!sendDoc}
        doc={sendDoc}
        defaultEmail={sendDoc ? defaultEmail || sendDoc.bookings?.[0]?.email_address || '' : ''}
        onHide={() => setSendDoc(null)}
        onSent={onChanged}
      />

      <BillingoCreateSzamlaModal
        doc={szamlaDoc}
        show={!!szamlaDoc}
        onHide={() => setSzamlaDoc(null)}
        onCreated={onChanged}
      />

      <StornoModal
        documents={stornoDocs}
        title={stornoDocs ? `Cancel (sztornó) ${stornoDocs[0].number}?` : ''}
        show={!!stornoDocs}
        onHide={() => setStornoDocs(null)}
        onDone={onChanged}
      />

      <BillingoDocumentDetailsModal
        doc={detailsDoc}
        show={!!detailsDoc}
        onHide={closeDetails}
        error={alert}
        actions={detailsDoc ? actionsOf(detailsDoc).map((action) => (action.keepsDetailsOpen
          ? action
          : { ...action, run: () => { closeDetails(); action.run(); } })) : []}
      />
    </>
  );

  return {
    actionsFor, openDetails: setDetailsDoc, alert, modals,
  };
}
